import React, { useMemo } from 'react';
import { GoogleMap, Polyline, Marker, useLoadScript } from '@react-google-maps/api';

interface GpsPoint {
  latitude: number;
  longitude: number;
  timestamp: string;
  accuracy?: number;
}

interface GpsRouteMapProps {
  gpsPoints: GpsPoint[];
  height?: string;
}

const libraries: ("drawing" | "geometry" | "places" | "visualization")[] = ['geometry'];

const GpsRouteMap: React.FC<GpsRouteMapProps> = ({ gpsPoints, height = '500px' }) => {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
  console.log('🔵 [GpsRouteMap] API Key present:', !!apiKey, 'Points:', gpsPoints?.length || 0);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });
  
  console.log('🔵 [GpsRouteMap] isLoaded:', isLoaded, 'loadError:', loadError);

  // Convert GPS points to Google Maps LatLng format
  const path = useMemo(() => {
    if (!gpsPoints || gpsPoints.length === 0) return [];
    return gpsPoints.map((point) => ({
      lat: point.latitude,
      lng: point.longitude,
    }));
  }, [gpsPoints]);

  // Calculate center and bounds
  const center = useMemo(() => {
    if (path.length === 0) {
      return { lat: 37.7749, lng: -122.4194 }; // Default to San Francisco
    }
    const avgLat = path.reduce((sum, p) => sum + p.lat, 0) / path.length;
    const avgLng = path.reduce((sum, p) => sum + p.lng, 0) / path.length;
    return { lat: avgLat, lng: avgLng };
  }, [path]);

  // Get start and end points for markers
  const startPoint = path.length > 0 ? path[0] : null;
  const endPoint = path.length > 1 ? path[path.length - 1] : null;

  const mapContainerStyle = {
    width: '100%',
    height: height,
  };

  const defaultOptions = {
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true,
  };

  if (!apiKey) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: '#d32f2f' }}>Google Maps API key is not configured. Please set VITE_GOOGLE_MAPS_API_KEY in your .env file.</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: '#d32f2f' }}>Error loading Google Maps: {loadError.message || 'Please check your API key configuration.'}</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading map...</p>
      </div>
    );
  }

  if (path.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>No GPS data to display</p>
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={center}
      zoom={13}
      options={defaultOptions}
    >
      {/* Draw polyline connecting all GPS points */}
      {path.length > 1 && (
        <Polyline
          path={path}
          options={{
            strokeColor: '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 4,
            geodesic: true,
          }}
        />
      )}

      {/* Start point marker */}
      {startPoint && isLoaded && (
        <Marker
          position={startPoint}
          label={{
            text: 'S',
            color: '#FFFFFF',
            fontWeight: 'bold',
          }}
          icon={{
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="6" fill="#00FF00" stroke="#FFFFFF" stroke-width="2"/>
              </svg>
            `),
            scaledSize: new google.maps.Size(16, 16),
            anchor: new google.maps.Point(8, 8),
          }}
        />
      )}

      {/* End point marker */}
      {endPoint && startPoint !== endPoint && isLoaded && (
        <Marker
          position={endPoint}
          label={{
            text: 'E',
            color: '#FFFFFF',
            fontWeight: 'bold',
          }}
          icon={{
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="6" fill="#FF0000" stroke="#FFFFFF" stroke-width="2"/>
              </svg>
            `),
            scaledSize: new google.maps.Size(16, 16),
            anchor: new google.maps.Point(8, 8),
          }}
        />
      )}

      {/* Single point marker if only one point */}
      {path.length === 1 && isLoaded && (
        <Marker
          position={path[0]}
          icon={{
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="6" fill="#0000FF" stroke="#FFFFFF" stroke-width="2"/>
              </svg>
            `),
            scaledSize: new google.maps.Size(16, 16),
            anchor: new google.maps.Point(8, 8),
          }}
        />
      )}
    </GoogleMap>
  );
};

export default GpsRouteMap;
